/**
 * Session-based voice chat history storage.
 * Replaces the broken merge-on-every-message approach.
 */

export interface VoiceMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  category?: string;
  card?: VoiceMessageCard;
  isError?: boolean;
}

export interface VoiceMessageCard {
  type: 'invoice' | 'report' | 'expense' | 'confirmation';
  title: string;
  subtitle?: string;
  amount?: string;
  status?: string;
  meta?: Record<string, string>;
}

export interface VoiceSession {
  id: string;
  title: string;
  messages: VoiceMessage[];
  createdAt: string;
  updatedAt: string;
  /** Page where this conversation was started — used for assistant context. */
  origin?: VoiceSessionOrigin;
}

export interface VoiceSessionOrigin {
  pathname: string;
  search: string;
  pageKey: string;
}

const STORAGE_KEY = 'voiceAssistantSessions';
const CURRENT_SESSION_KEY = 'voiceAssistantCurrentSessionId';
const MAX_SESSIONS = 50;
const MAX_MESSAGES_PER_SESSION = 100;

function parseSessions(): VoiceSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSessions(sessions: VoiceSession[]): void {
  const trimmed = sessions.slice(0, MAX_SESSIONS);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
}

export function getCurrentSessionId(): string | null {
  return localStorage.getItem(CURRENT_SESSION_KEY);
}

export function setCurrentSessionId(id: string): void {
  localStorage.setItem(CURRENT_SESSION_KEY, id);
}

export function createSession(title = 'New conversation', origin?: VoiceSessionOrigin): VoiceSession {
  const now = new Date().toISOString();
  const session: VoiceSession = {
    id: `vs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title,
    messages: [],
    createdAt: now,
    updatedAt: now,
    ...(origin ? { origin } : {}),
  };
  const sessions = parseSessions();
  sessions.unshift(session);
  saveSessions(sessions);
  setCurrentSessionId(session.id);
  return session;
}

export function getOrCreateCurrentSession(origin?: VoiceSessionOrigin): VoiceSession {
  const currentId = getCurrentSessionId();
  const sessions = parseSessions();
  if (currentId) {
    const found = sessions.find(s => s.id === currentId);
    if (found) {
      if (!found.origin && origin) {
        found.origin = origin;
        saveSessions(sessions);
      }
      return found;
    }
  }
  return createSession('New conversation', origin);
}

export function getAllSessions(): VoiceSession[] {
  return parseSessions();
}

export function getSessionMessages(sessionId: string): VoiceMessage[] {
  const session = parseSessions().find(s => s.id === sessionId);
  return session?.messages ?? [];
}

export function updateSessionMessages(sessionId: string, messages: VoiceMessage[]): void {
  const sessions = parseSessions();
  const idx = sessions.findIndex(s => s.id === sessionId);
  if (idx === -1) return;

  const trimmed = messages.slice(-MAX_MESSAGES_PER_SESSION);
  const firstUser = trimmed.find(m => m.role === 'user');
  const title = firstUser
    ? (firstUser.content.length > 50 ? firstUser.content.slice(0, 50) + '…' : firstUser.content)
    : sessions[idx].title;

  sessions[idx] = {
    ...sessions[idx],
    title,
    messages: trimmed,
    updatedAt: new Date().toISOString(),
  };
  saveSessions(sessions);
}

export function archiveCurrentSession(): void {
  setCurrentSessionId('');
}

export function startNewSession(origin?: VoiceSessionOrigin): VoiceSession {
  archiveCurrentSession();
  return createSession('New conversation', origin);
}

export function loadSession(sessionId: string): VoiceSession | null {
  const session = parseSessions().find(s => s.id === sessionId);
  if (session) {
    setCurrentSessionId(sessionId);
  }
  return session ?? null;
}

export function deleteSession(sessionId: string): void {
  const sessions = parseSessions().filter(s => s.id !== sessionId);
  saveSessions(sessions);
  if (getCurrentSessionId() === sessionId) {
    archiveCurrentSession();
  }
}

export function clearAllSessions(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(CURRENT_SESSION_KEY);
}

/** Migrate legacy flat message array to session format */
export function migrateLegacyHistory(): void {
  const legacy = localStorage.getItem('voiceAssistantHistory');
  if (!legacy || parseSessions().length > 0) return;

  try {
    const messages: VoiceMessage[] = JSON.parse(legacy).map((m: any) => ({
      id: m.id || `legacy_${Date.now()}`,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp || m.savedAt || new Date().toISOString(),
    }));
    if (messages.length === 0) return;

    const session = createSession('Imported conversation');
    updateSessionMessages(session.id, messages);
    localStorage.removeItem('voiceAssistantHistory');
  } catch {
    // ignore
  }
}
